import { useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import type { HomeySnapshot, HomeyServerMessage, HomeySocketStatus } from "@/types/homey";
import { applyServerMessage } from "@/lib/homey-delta";
import { useHomeySocket } from "./useHomeySocket";

export interface UseHomeyResult {
  snapshot: HomeySnapshot | undefined;
  isLoading: boolean;
  socketStatus: HomeySocketStatus;
  homeyStatus: "connected" | "disconnected";
  hasConnection: boolean;
}

/**
 * Composes the tRPC Homey queries with the /ws realtime socket.
 *
 * - getConnection is always fetched; getSnapshot is only enabled when a
 *   connection row exists with a non-error status.
 * - Incoming socket messages are applied to the snapshot cache via
 *   utils.homey.getSnapshot.setData so components re-render on deltas.
 * - homeyStatus tracks the last homey.status message; defaults to "connected"
 *   after the initial hello is received.
 */
export function useHomey(): UseHomeyResult {
  const utils = trpc.useUtils();

  const [socketStatus, setSocketStatus] = useState<HomeySocketStatus>("connecting");
  const [homeyStatus, setHomeyStatus] = useState<"connected" | "disconnected">("connected");

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  const { data: connection, isLoading: connectionLoading } = trpc.homey.getConnection.useQuery(
    undefined,
    { retry: false },
  );

  const hasConnection = connection !== null && connection !== undefined;

  const { data: snapshot, isLoading: snapshotLoading } = trpc.homey.getSnapshot.useQuery(
    undefined,
    {
      enabled: hasConnection,
      retry: false,
    },
  );

  // ------------------------------------------------------------------
  // Socket message handler (stable reference via useCallback)
  // ------------------------------------------------------------------

  const handleMessage = useCallback(
    (msg: HomeyServerMessage) => {
      // Track homey connectivity status from the server
      if (msg.type === "homey.status") {
        setHomeyStatus(msg.status);
        return;
      }

      // hello: no cache change needed — server sends it on connect
      if (msg.type === "hello") return;

      // For all other messages, patch the snapshot in the React Query cache
      utils.homey.getSnapshot.setData(undefined, (old: HomeySnapshot | undefined) =>
        old ? applyServerMessage(old, msg) : old,
      );
    },
    [utils],
  );

  // ------------------------------------------------------------------
  // Socket
  // ------------------------------------------------------------------

  useHomeySocket({
    onMessage: handleMessage,
    onStatusChange: setSocketStatus,
    enabled: hasConnection,
  });

  // ------------------------------------------------------------------
  // Derived loading state
  // ------------------------------------------------------------------

  const isLoading = connectionLoading || (hasConnection && snapshotLoading);

  return {
    snapshot,
    isLoading,
    socketStatus,
    homeyStatus,
    hasConnection,
  };
}
